import { formatStoredPath, parseStoredPath } from "../domain/paths";
import { openLocalDatabase, type RecentBoardRecord } from "../persistence/local_database";
import { remapWorkspacePath } from "./workspace_move";

export interface RecentBoardPersistence {
  delete(id: string): Promise<void>;
  list(): Promise<readonly RecentBoardRecord[]>;
  put(record: RecentBoardRecord): Promise<void>;
}

/** Persists recent-board records in browser-local support storage. */
class IndexedDbRecentBoardPersistence implements RecentBoardPersistence {
  /** Deletes one obsolete workspace/path hint by its stable key. */
  public async delete(id: string): Promise<void> {
    await (await openLocalDatabase()).delete("recent_boards", id);
  }

  /** Lists records without inspecting the canonical workspace. */
  public async list(): Promise<readonly RecentBoardRecord[]> {
    return (await openLocalDatabase()).getAll("recent_boards");
  }

  /** Replaces one workspace/path record by stable identity. */
  public async put(record: RecentBoardRecord): Promise<void> {
    await (await openLocalDatabase()).put("recent_boards", record);
  }
}

/** Tracks recently opened boards without making browser storage canonical. */
export class RecentBoardStore {
  private readonly persistence: RecentBoardPersistence;

  /** Uses IndexedDB in production and deterministic injected storage in tests. */
  public constructor(persistence: RecentBoardPersistence = new IndexedDbRecentBoardPersistence()) {
    this.persistence = persistence;
  }

  /** Returns the most recent normalized paths for one workspace identity. */
  public async list(workspaceId: string, limit = 8): Promise<readonly RecentBoardRecord[]> {
    const records = (await this.persistence.list())
      .filter((record) => record.workspaceId === workspaceId)
      .filter((record) => {
        try {
          parseStoredPath(record.boardPath);
          return true;
        } catch {
          return false;
        }
      })
      .sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt));
    return records.slice(0, Math.max(0, limit));
  }

  /** Records an opened board with one stable workspace/path key. */
  public async remember(
    workspaceId: string,
    path: readonly string[],
    openedAt: Date,
  ): Promise<RecentBoardRecord> {
    const boardPath = formatStoredPath(path);
    const record: RecentBoardRecord = {
      id: JSON.stringify([workspaceId, boardPath]),
      workspaceId,
      boardPath,
      lastOpenedAt: openedAt.toISOString(),
    };
    await this.persistence.put(record);
    return record;
  }

  /** Replaces every matching recent path after a move without accumulating stale keys. */
  public async remapPathPrefix(
    workspaceId: string,
    sourcePath: readonly string[],
    destinationPath: readonly string[] | null,
  ): Promise<void> {
    let records: readonly RecentBoardRecord[];
    try {
      records = (await this.persistence.list()).filter(
        (record) => record.workspaceId === workspaceId,
      );
    } catch {
      // Recent hints must never make a completed canonical filesystem move appear to fail.
      return;
    }
    for (const record of records) {
      let boardPath: readonly string[];
      try {
        boardPath = parseStoredPath(record.boardPath);
      } catch {
        await this.persistence.delete(record.id).catch(() => undefined);
        continue;
      }
      if (remapWorkspacePath(boardPath, sourcePath, sourcePath) === null) {
        continue;
      }
      try {
        await this.persistence.delete(record.id);
      } catch {
        continue;
      }
      if (destinationPath === null) {
        continue;
      }
      const remapped = remapWorkspacePath(boardPath, sourcePath, destinationPath);
      if (remapped === null) {
        continue;
      }
      const storedPath = formatStoredPath(remapped);
      await this.persistence
        .put({
          ...record,
          id: JSON.stringify([workspaceId, storedPath]),
          boardPath: storedPath,
        })
        .catch(() => undefined);
    }
  }
}
